import type { InjectionKey } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'

import { CMS } from '~/scopes/cms/routes-names'
import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'
import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'

import { ROUTES as REVIEWS_ROUTES } from '../route-names'

import seoTextTranslations from './translations/SeoText.translations'
import translations from './translations/reviews.translations'

export const MAX_RATING = 5

export const MAX_COMMENT_LENGTH = 1200

export const TRACKING_ZONE = 'reviews'

export const REVIEWS_LIST_DEFAULT_LENGTH = 10

export const MAX_REVIEWS_DISPLAYED_IN_LIST = 7

export const scrollToTargetKey = Symbol('scrolltoTarget') as InjectionKey<
  () => void
>

export const reviewsRequestEndpoints = {
  [PRODUCT_ROUTES.MASTER_PRODUCT_PAGE]: {
    getReviews: '/reviews/v1/products/:uuid/reviews',
    getRating: '/reviews/products/:uuid/reviews-summary',
    getRatingDetailsSummary: '/reviews/products/:uuid/reviews/details-summary',
  },
  [PRODUCT_ROUTES.VARIANT_PRODUCT_PAGE]: {
    getReviews: '/reviews/v1/products/:uuid/reviews',
    getRating: '/reviews/products/:uuid/reviews-summary',
    getRatingDetailsSummary: '/reviews/products/:uuid/reviews/details-summary',
  },
  [PRODUCT_LIST_ROUTES.PRODUCT_LIST]: {
    getReviews: '/reviews/v1/product-landings/:uuid/products/reviews',
    getRating: '/reviews/product-landings/:uuid/products/reviews-summary',
    getRatingDetailsSummary:
      '/reviews/product-landings/:uuid/products/reviews/details-summary',
  },
  [PRODUCT_LIST_ROUTES.SELLER_PRODUCT_LIST]: {
    getReviews: '/reviews/v1/sellers/:uuid/products/reviews',
  },
  [REVIEWS_ROUTES.PAGES.ALL]: {
    getReviews: '/reviews/v1/products/reviews',
    getRating: '/reviews/products/reviews-summary',
  },
  [REVIEWS_ROUTES.PAGES.BATTERY]: {
    getReviews: '/reviews/v1/products/reviews',
    getRating: '/reviews/products/reviews-summary',
  },
} as const

export const reviewsConfig = {
  [PRODUCT_ROUTES.MASTER_PRODUCT_PAGE]: {
    headers: { 'BM-Page-Name': PRODUCT_ROUTES.MASTER_PRODUCT_PAGE },
    parameters: { hasAIReviewsSummary: true },
  },
  [PRODUCT_ROUTES.VARIANT_PRODUCT_PAGE]: {
    headers: { 'BM-Page-Name': PRODUCT_ROUTES.VARIANT_PRODUCT_PAGE },
    parameters: { hasAIReviewsSummary: true },
  },
  [REVIEWS_ROUTES.PAGES.BATTERY]: {
    reviewsListQuery: { tags__in: 'BATTERY' },
  },
} as const

export type SeoTextLink = {
  slotName: string
  label: I18nDefinition
  to: RouteLocationRaw
}

type ReviewsPageData = {
  breadcrumbCurrentTitle: I18nDefinition
  breadcrumbParent?: { routeName: string; title: I18nDefinition }
  seoTextMainTitle: I18nDefinition
  seoTitle: I18nDefinition
  seoDescription: I18nDefinition
  seoTextBody: I18nDefinition
  seoTextHiddenBody: I18nDefinition
  seoTextLinks: SeoTextLink[]
}

export const REVIEWS_PAGE_CONTENT_BY_ROUTE: Record<string, ReviewsPageData> = {
  [REVIEWS_ROUTES.PAGES.ALL]: {
    breadcrumbCurrentTitle: translations.breadcrumbPageAllProducts,
    seoTextMainTitle: translations.titleAllProducts,
    seoTitle: translations.SEOTitleAllProducts,
    seoDescription: translations.SEODescriptionAllProducts,
    seoTextBody: seoTextTranslations.seoText,
    seoTextHiddenBody: seoTextTranslations.seoTextHiddenPart,
    seoTextLinks: [
      {
        slotName: 'link',
        label: seoTextTranslations.aboutUs,
        to: { name: CMS.ABOUT_US },
      },
      {
        slotName: 'batteryLink',
        label: seoTextTranslations.batteryLink,
        to: { name: REVIEWS_ROUTES.PAGES.BATTERY },
      },
    ],
  },
  [REVIEWS_ROUTES.PAGES.BATTERY]: {
    breadcrumbCurrentTitle: translations.breadcrumbPageBattery,
    breadcrumbParent: {
      routeName: REVIEWS_ROUTES.PAGES.ALL,
      title: translations.breadcrumbPageAllProducts,
    },
    seoTextMainTitle: translations.titleBattery,
    seoTitle: translations.SEOTitleBattery,
    seoDescription: translations.SEODescriptionBattery,
    seoTextBody: seoTextTranslations.seoTextBattery,
    seoTextHiddenBody: seoTextTranslations.seoTextHiddenPartBattery,
    seoTextLinks: [
      {
        slotName: 'link',
        label: seoTextTranslations.aboutUs,
        to: { name: CMS.ABOUT_US },
      },
    ],
  },
} as const
